import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageSquareText, X, Send, Loader2, Sparkles } from "lucide-react";

const GREETING = { role: "assistant", content: "Hi! 👋 I'm the Qwerjoh assistant. Ask me anything about getting started, our plans, or how it all works." };

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(`asst-${Math.random().toString(36).slice(2)}`);
  const endRef = useRef();

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (e, override) => {
    e?.preventDefault();
    const text = (override ?? input).trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { messages: next.filter((m) => m.role !== "system"), session_id: sessionRef.current });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I had trouble there. Please try again or message us on WhatsApp." }]);
    } finally { setLoading(false); }
  };

  const quick = ["Which plan is best for a beginner?", "How does it work?", "Can I really use just my phone?"];

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="assistant-toggle"
        aria-label="Ask Qwerjoh AI assistant"
        className="fixed bottom-24 right-5 z-50 grid place-items-center h-14 w-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F0D77B] text-[#0A0A0A] shadow-lg hover:scale-105 transition-transform duration-300 gold-glow"
      >
        {open ? <X size={24} /> : <MessageSquareText size={24} />}
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-40 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col rounded-2xl overflow-hidden border border-[#D4AF37]/30 bg-[#0d0d0d] shadow-2xl">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/15 to-transparent">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[#D4AF37]/20 text-[#D4AF37]"><Sparkles size={18} /></span>
            <div>
              <div className="text-sm font-medium text-white">Ask Qwerjoh</div>
              <div className="text-[11px] text-white/45">AI assistant · replies in seconds</div>
            </div>
          </div>

          <div data-testid="assistant-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-gradient-to-r from-[#D4AF37] to-[#F0D77B] text-[#0A0A0A]" : "bg-white/[0.06] text-white/90 border border-white/10"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="rounded-2xl bg-white/[0.06] border border-white/10 px-3.5 py-2.5"><Loader2 className="animate-spin text-[#D4AF37]" size={16} /></div></div>}
            {messages.length <= 1 && (
              <div className="pt-1 flex flex-wrap gap-2">
                {quick.map((q) => (
                  <button key={q} onClick={() => send(null, q)}
                    className="text-xs rounded-full border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors">{q}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              data-testid="assistant-input" placeholder="Type your question…"
              className="flex-1 rounded-full bg-black/50 border border-white/10 px-4 py-2.5 text-sm text-white outline-none focus:border-[#D4AF37]"
            />
            <button type="submit" disabled={loading || !input.trim()} data-testid="assistant-send"
              className="grid place-items-center h-10 w-10 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F0D77B] text-[#0A0A0A] disabled:opacity-40">
              <Send size={17} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
